class DtailsShopifyFormBooking extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;

    this.initialized = true;
    this.selectedTime = null;
    this.boundTimeslotFields = new WeakSet();
    this.boundForms = new WeakSet();
    this.dateLabel = this.querySelector('[data-booking-date-label]');
    this.formContainer = this.querySelector('[data-booking-form-container]');
    this.status = this.querySelector('[data-booking-status]');
    this.timeButtons = [...this.querySelectorAll('[data-booking-time]')];

    this.renderDate();

    for (const button of this.timeButtons) {
      button.addEventListener('click', () => this.selectTime(button));
    }

    this.prepareTimeslotField();
  }

  get fieldLabel() {
    return this.dataset.timeslotFieldLabel?.trim() || 'Timeslot';
  }

  get locale() {
    return this.dataset.locale || document.documentElement.lang || 'da-DK';
  }

  get timeZone() {
    return this.dataset.timeZone?.trim() || 'Europe/Copenhagen';
  }

  get timeslotRequiredMessage() {
    return this.dataset.timeslotRequiredMessage || 'Choose a timeslot before submitting the form.';
  }

  parseBookingDate() {
    const match = this.dataset.bookingDate?.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (!match) return null;

    const [, year, month, day] = match;
    const date = new Date(Date.UTC(Number(year), Number(month) - 1, Number(day), 12));

    return Number.isNaN(date.getTime()) ? null : date;
  }

  formatDate() {
    const date = this.parseBookingDate();
    if (!date) return this.dataset.bookingDate || '';

    return new Intl.DateTimeFormat(this.locale, {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      timeZone: this.timeZone,
    }).format(date);
  }

  renderDate() {
    if (this.dateLabel) this.dateLabel.textContent = this.formatDate();
  }

  selectTime(selectedButton) {
    this.selectedTime = selectedButton.dataset.bookingTime;

    for (const button of this.timeButtons) {
      const selected = button === selectedButton;
      button.classList.toggle('btn--primary', selected);
      button.classList.toggle('btn--secondary', !selected);
      button.setAttribute('aria-pressed', String(selected));
    }

    this.clearStatus();
    this.syncTimeslot();
  }

  formatTimeslotValue() {
    return `${this.formatDate()} · ${this.selectedTime} (${this.timeZone})`;
  }

  async prepareTimeslotField() {
    const field = await this.waitForTimeslotField();
    if (field) this.lockTimeslotField(field);
  }

  async syncTimeslot() {
    const selectedTime = this.selectedTime;
    const field = await this.waitForTimeslotField();

    if (!field) {
      if (this.selectedTime === selectedTime) this.showIntegrationError();
      return;
    }

    this.lockTimeslotField(field);
    this.setNativeValue(field, this.formatTimeslotValue());
    field.setCustomValidity('');
    field.dispatchEvent(new InputEvent('input', { bubbles: true, composed: true, inputType: 'insertText' }));
    field.dispatchEvent(new Event('change', { bubbles: true, composed: true }));
  }

  lockTimeslotField(field) {
    field.required = true;
    field.setCustomValidity(field.value.trim() ? '' : this.timeslotRequiredMessage);

    if (field instanceof HTMLInputElement || field instanceof HTMLTextAreaElement) {
      field.readOnly = true;
      field.setAttribute('aria-readonly', 'true');
    }

    this.bindFormValidation(field);

    if (this.boundTimeslotFields.has(field)) return;

    field.addEventListener('invalid', (event) => {
      if (field.value.trim()) return;

      event.preventDefault();
      this.showTimeslotRequired();
    });
    this.boundTimeslotFields.add(field);
  }

  bindFormValidation(field) {
    const form = field.closest('form');
    if (!form || this.boundForms.has(form)) return;

    const blockSubmitWithoutTimeslot = (event) => {
      const currentField = this.findTimeslotField();
      if (currentField?.value.trim()) return;

      currentField?.setCustomValidity(this.timeslotRequiredMessage);
      event.preventDefault();
      event.stopImmediatePropagation();
      this.showTimeslotRequired();
    };

    form.addEventListener('submit', blockSubmitWithoutTimeslot, true);
    form.addEventListener(
      'click',
      (event) => {
        const target = event.target instanceof Element ? event.target : null;
        const submitButton = target?.closest(
          'button[type="submit"], input[type="submit"], [data-testid="btn-form-submit"]'
        );

        if (submitButton) blockSubmitWithoutTimeslot(event);
      },
      true
    );
    this.boundForms.add(form);
  }

  async waitForTimeslotField() {
    const timeoutAt = Date.now() + 5000;

    while (Date.now() < timeoutAt) {
      const field = this.findTimeslotField();
      if (field) return field;
      await new Promise((resolve) => window.setTimeout(resolve, 100));
    }

    return null;
  }

  findTimeslotField() {
    const formEmbed = this.formContainer?.querySelector('shopify-forms-embed');
    if (!formEmbed?.shadowRoot) return null;

    const roots = this.getOpenRoots(formEmbed.shadowRoot);
    const normalizedTarget = this.normalizeLabel(this.fieldLabel);

    for (const root of roots) {
      for (const label of root.querySelectorAll('label')) {
        if (this.normalizeLabel(label.textContent) !== normalizedTarget) continue;

        const field =
          label.control ||
          (label.htmlFor ? root.getElementById(label.htmlFor) : null) ||
          label.querySelector('input, textarea, select');

        if (field) return field;
      }
    }

    for (const root of roots) {
      for (const field of root.querySelectorAll('input:not([type="hidden"]), textarea, select')) {
        const identifiers = [
          field.getAttribute('aria-label'),
          field.getAttribute('placeholder'),
          field.getAttribute('name'),
          field.id,
        ];

        if (identifiers.some((identifier) => this.normalizeLabel(identifier) === normalizedTarget)) {
          return field;
        }
      }
    }

    return null;
  }

  getOpenRoots(initialRoot) {
    const roots = [initialRoot];

    for (let index = 0; index < roots.length; index += 1) {
      for (const element of roots[index].querySelectorAll('*')) {
        if (element.shadowRoot) roots.push(element.shadowRoot);
      }
    }

    return roots;
  }

  normalizeLabel(value) {
    return (value || '')
      .replace(/\*/g, '')
      .replace(/\s+/g, ' ')
      .trim()
      .toLocaleLowerCase(this.locale);
  }

  setNativeValue(field, value) {
    const prototype =
      field instanceof HTMLTextAreaElement
        ? HTMLTextAreaElement.prototype
        : field instanceof HTMLSelectElement
          ? HTMLSelectElement.prototype
          : HTMLInputElement.prototype;
    const valueSetter = Object.getOwnPropertyDescriptor(prototype, 'value')?.set;

    if (valueSetter) {
      valueSetter.call(field, value);
    } else {
      field.value = value;
    }
  }

  clearStatus() {
    if (!this.status) return;

    this.status.hidden = true;
    this.status.textContent = '';
  }

  showIntegrationError() {
    if (this.status) {
      this.status.textContent =
        this.dataset.integrationError || 'The selected timeslot could not be transferred to the form.';
      this.status.hidden = false;
    }

    console.error(`[DTAILS Shopify Forms booking] Field "${this.fieldLabel}" was not found.`);
  }

  showTimeslotRequired() {
    if (this.status) {
      this.status.textContent = this.timeslotRequiredMessage;
      this.status.hidden = false;
    }

    this.timeButtons[0]?.focus();
  }
}

if (!customElements.get('dtails-shopify-form-booking')) {
  customElements.define('dtails-shopify-form-booking', DtailsShopifyFormBooking);
}
